import React from 'react';
import cx from 'classnames';
import { useDebouncedCallback } from '@react-hookz/web';
import { useRouter } from 'next/router';
import Icon, { IconName } from 'components/icon/icon';
import ActiveLink from 'components/active-link/active-link';
import PageNavigation from 'components/page/page-navigation';
import SearchField from 'components/search-field/search-field';
import navigationLexemes from 'components/lexemes/navigation';
import headerLexemes from 'components/lexemes/header';
import useMediaQuery, { MediaQueryWidth } from 'lib/use-media-query';
import * as Scroll from 'react-scroll';
import { Navigation } from 'schema/frontend/types';
import LocaleContext from 'lib/locale-context';

const navItems = [
    {
        title: 'Hero',
        type: 'hero',
        url: 'hero',
        isHidden: true,
    },
    {
        title: 'Video',
        type: 'video',
        url: 'video',
        isHidden: true,
    },
    {
        title: navigationLexemes.lxNavigationHandBookTitle,
        type: 'handbook',
        url: 'guides',
    },
    {
        title: navigationLexemes.lxNavigationGuidesTitle,
        type: 'guides',
        url: 'guides',
    },
    {
        title: navigationLexemes.lxNavigationToolsTitle,
        type: 'tools',
        url: 'tools',
    },
    {
        title: navigationLexemes.lxNavigationHelplinesTitle,
        type: 'helplines',
        url: 'helplines',
        isActive: true,
    },
    {
        title: navigationLexemes.lxNavigationUpdatesTitle,
        type: 'updates',
        url: 'updates',
    },
];

type NavigationList = {
    type?: string;
    isHidden?: boolean;
    url?: string;
    title?: string;
}[];

type Props = {
    logo?: React.ReactNode;
    search?: React.ReactNode;
    hasMenu?: boolean;
    extraClass?: string;
    bgColor: string;
    hasBackgroundColor?: boolean;
    hasPhysicalSpace?: boolean;
    hasBorder?: boolean;
    hasLogoUrl?: boolean;
    navigation?: Navigation;
};

const PageHeader: React.FC<Props> = ({
    extraClass,
    hasMenu = true,
    bgColor,
    hasBackgroundColor,
    hasPhysicalSpace,
    hasBorder,
    hasLogoUrl,
    navigation,
}) => {
    const lang = React.useContext(LocaleContext);
    const router = useRouter();
    const isMobile = useMediaQuery(768);
    const isTablet = useMediaQuery(MediaQueryWidth['desktop-md']);
    const [hasBackground, setBackground] = React.useState(hasBackgroundColor);
    const [showSearch, setSearchVisible] = React.useState(false);
    const [showMenu, setMenuVisible] = React.useState(false);

    const headerClassName = cx({
        'page-header': true,
        'has-background': hasBackground,
        [extraClass as string]: extraClass,
        'is-mobile': isTablet,
        'has-border': hasBorder,
    });

    // eslint-disable-next-line no-nested-ternary
    const headerHeight = isMobile ? 58 : isTablet ? 100 : 123;

    React.useEffect(() => {
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }, [headerHeight]);

    React.useEffect(() => {
        if (router.route === '/[lang]' || router.route === '/') {
            setSearchVisible(false);
        } else {
            setSearchVisible(true);
        }
    }, [router.route]);

    const onScroll = useDebouncedCallback(
        () => {
            if (router.route === '/[lang]' || router.route === '/') {
                const threshold = 100;
                const scrollTop = global.window.scrollY;
                setBackground(scrollTop > threshold);
            }
        },
        [],
        10
    );

    React.useEffect(() => {
        window.addEventListener('scroll', onScroll);

        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [onScroll]);

    // @ts-ignore
    const navigationList: NavigationList = navItems
        .map((item) => {
            const stringTitle =
                typeof item.title !== 'string' ? item.title() : (item.title as string);
            item.title = stringTitle;

            // exception for handbook
            if (item.type === 'handbook') {
                item.url = 'https://badoocdn.com/i/big/badoo/safety/en/badoo-safety-handbook.pdf';

                if (lang === 'de') {
                    item.url =
                        'https://badoocdn.com/i/big/badoo/safety/de/badoo-safety-handbook.pdf';
                }

                if (lang === 'es') {
                    item.url =
                        'https://badoocdn.com/i/big/badoo/safety/de/badoo-safety-handbook.pdf';
                }

                if (lang === 'fr') {
                    item.url =
                        'https://badoocdn.com/i/big/badoo/safety/fr/badoo-safety-handbook.pdf';
                }

                return item;
            }

            if (item.type === 'guides') {
                item.isHidden = !Boolean(navigation?.guides);
                return item;
            }

            if (item.type === 'tools') {
                item.isHidden = !Boolean(navigation?.tools);
                return item;
            }

            if (item.type === 'helplines') {
                item.isHidden = !Boolean(navigation?.helplines);
                return item;
            }

            if (item.type === 'updates') {
                item.isHidden = !Boolean(navigation?.updates);
                return item;
            }

            return item;
        })
        .filter((item) => item);

    return (
        <>
            <div
                className={headerClassName}
                style={{
                    ['--header-bg-color' as string]: `${bgColor}`,
                }}
            >
                <div className="page-header__inner">
                    {hasLogoUrl ? (
                        <ActiveLink activeClassName="active">
                            <a className="page-header__logo has-url" href={`/${lang}`}>
                                <Icon
                                    name={IconName.Logo}
                                    a11y={{
                                        label: headerLexemes.lxHeaderLogoA11yLabel({
                                            partner: 'Badoo',
                                        }),
                                    }}
                                />
                            </a>
                        </ActiveLink>
                    ) : (
                        <button
                            className="page-header__logo"
                            onClick={() => {
                                setMenuVisible(false);
                                Scroll.animateScroll.scrollToTop();
                            }}
                        >
                            <Icon
                                name={IconName.Logo}
                                a11y={{
                                    label: headerLexemes.lxHeaderLogoA11yLabel({
                                        partner: 'Badoo',
                                    }),
                                }}
                            />
                        </button>
                    )}

                    {hasMenu ? (
                        <div className="page-header__navigation">
                            <PageNavigation
                                offset={headerHeight}
                                onSetActive={(href: string) => {
                                    setSearchVisible(href !== 'hero');
                                }}
                                items={navigationList}
                                onChange={(isOpened) => setMenuVisible(isOpened)}
                                isOpened={showMenu}
                            />
                        </div>
                    ) : null}

                    {!isMobile && showSearch ? (
                        <div className="page-header__search">
                            <SearchField sizeSmall={true} />
                        </div>
                    ) : null}
                </div>
            </div>

            {hasPhysicalSpace && <div style={{ paddingTop: 'var(--header-height)' }} />}
        </>
    );
};

export default PageHeader;
